import React, { memo, useCallback, useEffect, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { injectIntl } from 'react-intl';
import { createSelector } from 'reselect'
// Cloud React components
import { Select } from 'Components';
import InputSearchMap from 'Components/Map/components/InputSearchMap';
import { useGeocoder } from 'Core/hooks';
import { getUnitsNaturalOrder } from 'Core/data/Helpers';
// Views
import EntityPicker from 'Modules/.common/components/EntityPicker';
// Utils
import { TypeObjectMap } from 'Components/Map/providers/enums';
import { TypeLayer } from 'Components/Map/providers/enums'
import { NotificationManager } from "Components/Notifications";
import { useDataGridThrottler } from 'Core/hooks';

// Actions
import {
  selectCreateClickMapType,
  addPointers,
  centerOnUnit,
  setLatLngRestart
} from 'Redux/actions';

import { unitSelectors } from 'Core/realtime/redux/unitSlice';
import { lastReadingSelectors } from 'Core/realtime/redux/lastReadingSlice';

const getUnitsCatalog = createSelector([state => unitSelectors.selectAll(state)], getUnitsNaturalOrder);
const getReadingsCatalog = (state) => lastReadingSelectors.selectEntities(state);
const getVehicleTags = (state) => state.realtimeMapUtilitiesVehiclesListRedux.tagsVehicleList;

/*********************************************************
 * Get information from pagination elements
*********************************************************/
const getData = createSelector(
  [getUnitsCatalog, getReadingsCatalog, getVehicleTags],
  (unitsList, readings, vehicleTags) => {
    let result = []
    unitsList?.forEach((unit) => {
      try {
        if (unit.id == "-1" || (!unit.id && !unit.CUnits_id)) return
        const idUnit = unit.id

        let lastReading = readings?.[idUnit]
        const lat = lastReading?.latitude || unit?.latitude || unit?.lat
        const lng = lastReading?.longitude || unit?.longitude || unit?.lng
        const label = unit?.label || lastReading?.deviceLabel;
        if (!Array.isArray(unit?.tags) || unit.tags.length === 0) {
          unit = { ...unit, tags: [{ color: "000000", hasChildren: false, id: -1, label: "Untagged", parentId: null, path: "" }] }
        }

        //filter unit for existing tag
        unit = {
          ...unit,
          tags: !vehicleTags?.length ? unit.tags : unit.tags?.filter(tag => vehicleTags?.some(vehicleTag => tag?.id == -1 || vehicleTag?.id === tag?.id))
        }

        // if the unit no have tags or none is the group "Untagged", it is omitted
        if (!unit?.tags?.length && !unit?.tags?.some(tag => tag.id === -1)) return; // No is entered on the list of vehicle because no have permisions

        if (!lastReading?.haveLatLng) return; // No is entered on the list of vehicle because no have lat and lng

        if (!lastReading?.address) return; // No is entered on the list of vehicle because no have address

        const item = {
          id: unit.id || lastReading?.deviceId,
          label: label,
          lat: lat ? parseFloat(lat) : 0,
          lng: lng ? parseFloat(lng) : 0,
          latitude: lat ? parseFloat(lat) : 0,
          longitude: lng ? parseFloat(lng) : 0,
          haveLatLng: lastReading?.haveLatLng, // this vehicle no have lat and lng, cant show on map
          address: lastReading?.address,
          fulladdress: lastReading?.address?.formatted || '',
          changeLabel: (unit?.label != label),
          changeLatLng: lastReading?.changeLatLng,
          lastReading: lastReading,
          tags: unit.tags || []
        }
        result.push(item);
      } catch (e) {
        console.log('e', e)
        NotificationManager.error('Error', `Error src/components/Map/components/FromToMap/index.js to create show list of vehicles - ${e}`, 3000, null, null, 'error');
      }
    });
    return result
  }
)

const SelectLocation = (props) => {
  const dispatch = useDispatch();
  const { intl, disabled, showInfoBubble, onChange, mapRef, restart } = props;
  const { messages } = intl;

  const addressTypes = [
    { label: messages['routes_Address'], value: "address" },
    { label: messages['routes_Unit'], value: "unit" },
    { label: messages['routes_Landmark'], value: "landmark" },
    { label: messages['routes_mapClick'], value: "click" }
  ];

  const loadingUnits = useSelector(state => state.catalogsRedux.loadingUnits);
  const clickLatLng = useSelector((state) => state.realtimeMapRedux.clickLatLng);

  const [formData, setFormData] = useState({ location: null });
  const [error, setError] = useState(null);
  const [onGetGeocoder, address] = useGeocoder();

  /*********************************************************
    * use compute data to sync values showing in table
  *********************************************************/
  const computedData = useSelector(state => getData(state) || []);
  const propertiesToValidate = useMemo(() => ["label", "lat", "lng", "changeLatLng", "haveLatLng", "tags"], []);

  // Hook handles both Throttled Arrays for `<Map/>` and imperative delta diffing for markers via `handleMapUpdates`
  const units = useDataGridThrottler(
    computedData,
    1000,
    () => { },
    "id",
    propertiesToValidate
  );

  useEffect(() => {
    return () => {
      setFormData({ location: null });
      setError(null);
      dispatch(setLatLngRestart())
    }
  }, []);

  useEffect(() => {
    if (disabled) {
      dispatch(selectCreateClickMapType());
    } else if (disabled === false && formData?.type?.value === "click") {
      dispatch(selectCreateClickMapType(TypeObjectMap.MapClick));
    }
  }, [disabled, formData?.type?.value]);

  useEffect(() => {
    if (clickLatLng) {
      addMarker(clickLatLng);
      onGetGeocoder({
        from: 'latlng',
        lat: clickLatLng.lat,
        lng: clickLatLng.lng
      });
      setError(null);
    }
  }, [clickLatLng]);

  useEffect(() => {
    if (address && address.result == true && address.data && address.data.length) {
      const { data } = address;
      const stop = {
        latitude: data[0]?.lat,
        longitude: data[0]?.lng,
        address: data[0]?.fulladdress,
        city: data[0]?.city,
        state: data[0]?.state
      };

      setFormData({ ...formData, location: stop });
    }
  }, [address]);

  useEffect(() => {
    if (formData) {
      if (onChange) onChange(formData);
    }
  }, [formData]);

  useEffect(() => {
    if (restart > 0) {
      dispatch(selectCreateClickMapType(TypeObjectMap.Default))
      dispatch(setLatLngRestart());
      setFormData({ location: null });
      setError(null);
    }
  }, [restart])

  const addMarker = useCallback((latLng) => {
    const marker = {
      id: `marker_select_location`,
      lat: latLng.lat,
      lng: latLng.lng,
      label: 'Map Point',
      heading: 0,
      color: '000',
      eventName: 'no event',
      draggable: false
    }
    dispatch(addPointers([]));
    dispatch(addPointers([marker]));
  });

  return (
    <div>
      <div className="form-group">
        <Select
          id="type"
          name="type"
          value={formData.type}
          label={messages['routes_selectAddressType']}
          placeholder={messages['routes_selectAddressType']}
          onChange={(value) => {
            setFormData({ ...formData, type: value, data: null, location: null });
            if (value && value?.value === "click") {
              dispatch(selectCreateClickMapType(TypeObjectMap.MapClick));
            } else {
              dispatch(selectCreateClickMapType(TypeObjectMap.Default));
            }
          }}
          isSearchable={false}
          required={true}
          requiredMessage={messages['required_field']}
          disabled={disabled}
          options={addressTypes}
          textField="label"
          valueField="value"
        />
      </div>

      <div style={{ marginTop: 10 }}>
        {
          (formData?.type && formData?.type?.value === "address")
          &&
          <div>
            <label className="input-normal-label">
              {messages['routes_selectAddress']} <span className="text-danger"> *</span>
            </label>

            <div style={{ marginTop: 5 }}>
              <InputSearchMap
                normalView
                noMap
                restart={restart}
                onGetAddress={(value) => {
                  if (value && value?.length) {
                    const address = value[0];

                    const stop = {
                      latitude: address?.lat,
                      longitude: address?.lng,
                      address: address?.fulladdress,
                      city: address?.city,
                      state: address?.state
                    };

                    setFormData({ ...formData, location: stop });
                    addMarker(address);
                  }
                  setError(null);
                }}
                onClear={() => {
                  setFormData({ ...formData, location: null });
                  dispatch(addPointers([]));
                  setError(null);
                }}
                draggable={false}
                disabled={disabled}
                placeholder={messages['search']}
                noOptionsText={messages['noOptions']}
              />
            </div>

            {
              error &&
              <small className="text-danger" style={{ paddingTop: 5 }}>
                {error}
              </small>
            }
          </div>
        }

        {
          (formData?.type && formData?.type?.value === "unit")
          &&
          <div>
            <Select
              id="unit"
              name="unit"
              label={messages['routes_selectUnit']}
              placeholder={messages['routes_selectUnit']}
              value={formData.data}
              disabled={disabled}
              onChange={(value) => {
                const { lastReading } = value;
                const { latitude, longitude, address } = lastReading;
                const stop = {
                  latitude: latitude,
                  longitude: longitude,
                  address: address?.address,
                  city: address?.city,
                  state: address?.state
                };

                setFormData({ ...formData, data: value, location: stop });
                if (showInfoBubble) {
                  dispatch(centerOnUnit(value.id));
                } else {
                  addMarker({ lat: latitude, lng: longitude });
                }
              }}
              loading={loadingUnits}
              options={units}
              textField={'label'}
              valueField={'id'}
              isSearchable
              required
              requiredMessage={messages['required_field']}
            />
          </div>
        }

        {
          (formData?.type && formData?.type?.value === "landmark")
          &&
          <div>
            <EntityPicker
              id="landmarks"
              name="landmarks"
              entity="landmarks"
              disabled={disabled}
              required
              value={formData?.data}
              onChange={(value) => {
                const stop = {
                  landmarkId: value?.id,
                  latitude: value?.latitude,
                  longitude: value?.longitude,
                  address: value?.address,
                  city: value?.city,
                  state: value?.state
                };

                setFormData({ ...formData, data: value, location: stop });
                if (showInfoBubble && mapRef) {
                  const coordinates = new google.maps.LatLng(value?.latitude, value?.longitude); // eslint-disable-line no-undef
                  const radius = value?.shape == "circle" ? value?.radius : 0
                  const coordinatesCenter = [{ lat: value?.latitude, lng: value?.longitude }];
                  mapRef.current.centerByFitBounds(coordinatesCenter, radius)
                  const mapObject = {
                    id: value?.id,
                    position: coordinates,
                    label: value?.name,
                    icon: {
                      rotation: 0
                    },
                    layerName: TypeLayer.Landmark
                  }


                  mapRef.current.modalPopupEvent(mapObject, mapObject);
                } else {
                  addMarker({ lat: value?.latitude, lng: value?.longitude });
                }
              }}
            />
            {
              error &&
              <small className="text-danger" style={{ paddingTop: 10 }}>
                {error}
              </small>
            }
          </div>
        }

        {
          (formData?.type && formData?.type?.value === "click")
          &&
          <div>
            {
              error &&
              <small className="text-danger" style={{ paddingTop: 5 }}>
                {error}
              </small>
            }
          </div>
        }
      </div>

    </div>
  )
}

export default injectIntl(memo(SelectLocation));
